import React, { useEffect, useState } from 'react';
import { Button, Card, Divider, Pagination, Modal, message } from 'antd';
import { useHistory, useLocation } from 'react-router';
import * as QueryString from 'query-string';
import styled from 'styled-components';

// components and utils
import { BrowseDataProductRelatedEntityType, DataDataProductTable } from './BrowseDataProductTableType';
import UpdateOwnersModal from '../entity/shared/containers/profile/sidebar/Ownership/UpdateOwnersModal';
import useBrowseDataProductTableRequest, { IPagination } from './useBrowseDataProductTableRequest';
import { SetDomainModal } from '../entity/shared/containers/profile/sidebar/Domain/SetDomainModal';
import CreateDataProductModal from '../entity/domain/DataProductsTab/CreateDataProductModal';
import { BrowseDataProductBulkActionAbleItems } from './BrowseDataProductBulkActions';
import EditTagTermsModal, { OperationType } from '../shared/tags/AddTagsTermsModal';
import { BrowseDataProductHeader } from './BrowseDataProductHeader';
import { DefaultValueType } from '../shared/tags/DefaultValueType';
import { BrowseDataProductTable } from './BrowseDataProductTable';
import { BulkAction } from '../shared/bulkAction/BulkAction';
import { useTranslation } from '../../i18n/useTranslation';
import { handleBatchError } from '../entity/shared/utils';
import { ModalKeys } from './BrowseDataProductModalKeys';
import { Notification } from '../shared/Notification';
import { scrollToTop } from '../shared/searchUtils';
import { BrowseCfg } from '../../conf';

// generated
import { EntityImportValidationMetadataInput, EntityType } from '../../types.generated';
import BulkLoadEntityModal from '../entity/shared/EntityDropdown/BulkLoadEntityModal';
import { useExportEntityMutation } from '../../graphql/mutations.generated';
import {
    useBatchDeleteDataProductsMutation,
    useGetEntityImportValidationMetadataLazyQuery,
} from '../../graphql/dataProduct.generated';
import { useRbac } from '../entity/shared/useRbac';

const PaginationContainer = styled.div`
    padding: 8px;
    .ant-pagination {
        display: flex;
        align-items: center;
        .ant-pagination-total-text {
            order: 1;
            margin: 0;
            flex-grow: 1;
            text-align: right;
        }
        .ant-pagination-options {
            order: -1;
            margin: 0;
            flex-grow: 1;
        }
    }
`;

export const BrowseDataProduct = () => {
    const [exportEntity, setExportEntity]: any = useState(null);
    const [modalKey, setModalKey] = useState<string | null>(null);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState<number>(BrowseCfg.PAGE_SIZES[0]);
    const [individualDPUrn, setIndividualDPUrn] = useState<string | null>(null);
    const [defaultValueURNs, setDefaultValueURNs] = useState<DefaultValueType[]>([]);
    const [isBulkImportModalVisible, setIsBulkImportModalVisible] = useState<boolean>(false);

    const [batchDeleteDataProductsMutation, { loading: loadingDelete }] = useBatchDeleteDataProductsMutation();
    const [getExpectedXlsxHeadersFromAPI, { data: expectedXlsxHeadersFromAPI }] =
        useGetEntityImportValidationMetadataLazyQuery();
    const [exportEntityMutation] = useExportEntityMutation();
    const { t } = useTranslation();

    const {
        canUpdateDataProductGlossaryTerms,
        canUpdateDataProductDomains,
        canUpdateDataProductOwners,
        canBulkImportDataProduct,
        canUpdateDataProductTags,
        canCreateDataProduct,
        canDeleteDataProduct,
        canExportDataProduct,
    } = useRbac();

    const canUpdateTerms = canUpdateDataProductGlossaryTerms();
    const canUpdateDomains = canUpdateDataProductDomains();
    const canUpdateOwners = canUpdateDataProductOwners();
    const canBulkImport = canBulkImportDataProduct();
    const canUpdateTags = canUpdateDataProductTags();
    const canCreate = canCreateDataProduct();
    const canDelete = canDeleteDataProduct();
    const canExport = canExportDataProduct();

    const location = useLocation();
    const history = useHistory();

    const rootPath = location.pathname;
    const params = QueryString.parse(location.search);

    const page: number = params.page && Number(params.page as string) > 0 ? Number(params.page as string) : 1;

    const pagination: IPagination = {
        start: (page - 1) * pageSize,
        count: pageSize,
    };

    useEffect(() => {
        const input: EntityImportValidationMetadataInput = {
            entityType: 'dataProduct',
        };
        getExpectedXlsxHeadersFromAPI({ variables: { input } });
    }, [getExpectedXlsxHeadersFromAPI]);

    /**
     * Resets selected items and "Select All" state when the browse path changes.
     * Only clear selections when actually navigating away from data products page,
     * not when just changing pages within the same view.
     */
    useEffect(() => {
        // Only reset selected items when navigating away from data products entirely
        // Don't reset when just changing pages (page parameter changes)
        const isPageChange = location.search.includes('page=');

        if (!isPageChange) {
            setSelectedItems([]);
        }
    }, [location.pathname, location.search]); // Track both pathname and search params

    // Function to close the modal
    const handleCloseModal = () => {
        setModalKey(null);
        if (individualDPUrn) {
            setIndividualDPUrn(null);
        }
        if (defaultValueURNs.length > 0) {
            setDefaultValueURNs([]);
        }
    };

    /**
     * Initiates the export of selected data products into an XLSX file.
     * Performs an asynchronous mutation to trigger the export process for the items
     * specified in the `selectedItems` state/variable.
     * Upon successful completion of the mutation, it updates the `exportEntity`
     * state with the export result and clears the list of selected items.
     */
    const exportXlsx = () => {
        const input = {
            dataProduct: {
                dataProductUrns: selectedItems,
            },
            entityType: 'dataProduct',
        };
        exportEntityMutation({
            variables: {
                input,
            },
        })
            .then((res) => {
                const { data } = res;
                setIsAllSelected(false);
                setSelectedItems([]);
                setExportEntity(data?.exportEntity);
                handleCloseModal();
            })
            .catch((e) => {
                if (e.networkError.statusCode === 503) {
                    const updatedInput = {
                        ...input,
                        async: true,
                    };
                    // Retry with a different input for 504 errors
                    exportEntityMutation({
                        variables: {
                            input: updatedInput,
                        },
                    })
                        .then((retryRes) => {
                            const { data } = retryRes;
                            setIsAllSelected(false);
                            setSelectedItems([]);
                            setExportEntity(data?.exportEntity);
                            handleCloseModal();
                        })
                        .catch((retryError) => {
                            message.error(`Export failed after retry. Please try again later ${retryError}`);
                        });
                }
            });
    };

    const {
        dataSource,
        total: totalCount,
        loading,
        onSearch,
        onApplyFilter,
        onResetFilter,
        fetchMore: fetchMoreDataProductListing,
        searchText: searchQuery,
        filterOptions: filters,
        refetch: refecthDataProductListing,
    } = useBrowseDataProductTableRequest({
        pagination,
    });

    /**
     * Update the "Select All" checkbox state when page data changes
     * to reflect the correct state for the current page
     */
    useEffect(() => {
        if (dataSource && dataSource.length > 0) {
            const currentPageItems = dataSource.map((item) => item?.id);
            const currentPageSelectedCount = currentPageItems.filter((id) => selectedItems.includes(id)).length;
            setIsAllSelected(currentPageSelectedCount === currentPageItems.length);
        } else {
            setIsAllSelected(false);
        }
    }, [dataSource, selectedItems]);

    /**
     * Fetches additional data when the user navigates to a new page or creates a new data product.
     */
    function fetchMore() {
        fetchMoreDataProductListing({
            variables: {
                input: {
                    start: pagination.start,
                    count: pagination.count,
                    query: searchQuery,
                    filters: filters.map((filter) => ({
                        field: filter.field === 'dataProducts' ? 'urn' : filter.field,
                        values: filter.values,
                        condition: filter.condition,
                    })),
                },
            },
        });
    }

    const onBulkActionCancel = () => {
        setIsAllSelected(false);
        setSelectedItems([]);
    };

    /**
     * Handles page change by updating the URL and scrolling to the top of the page.
     * @param newPage - The new page number to navigate to.
     */
    const onChangePage = (newPage: number) => {
        scrollToTop();
        history.push({
            pathname: rootPath,
            search: `&page=${newPage}`,
        });
    };

    /**
     * Handles the "Select All" checkbox functionality.
     * Updated to handle cross-page selections properly.
     * @param e - The event triggered by the checkbox.
     */
    const handleSelectAll = (e) => {
        const dpNames: DataDataProductTable[] = dataSource ? [...dataSource] : [];

        if (e.target.checked) {
            // Add all current page items to selection (avoid duplicates)
            const currentPageItems = dpNames.map((item) => item?.id);
            const updatedSelection = Array.from(new Set([...selectedItems, ...currentPageItems]));
            setSelectedItems(updatedSelection);
        } else {
            // Remove all current page items from selection
            const currentPageItems = dpNames.map((item) => item?.id);
            const updatedSelection = selectedItems.filter((id) => !currentPageItems.includes(id));
            setSelectedItems(updatedSelection);
        }

        // Update isAllSelected based on current page
        setIsAllSelected(e.target.checked);
    };

    /**
     * Applies a filter to the data product list.
     * @param key - The filter key.
     * @param value - The filter value.
     */
    const onApplyFilters = (key: string, value: string | number) => {
        onChangePage(1);
        onApplyFilter(key, value);
    };

    /**
     * Resets a specific filter type.
     * @param type - The type of filter to reset.
     */
    const onResetFilters = (type: string) => {
        onChangePage(1);
        onResetFilter(type);
    };

    /**
     * Handles the search functionality for data products.
     * @param args - The search arguments.
     */
    const onSearchItem = (...args: any[]) => {
        onChangePage(1);
        onSearch(...args);
    };

    /**
     * Toggles the selection of an individual data product.
     * Updated to handle cross-page selections properly.
     * @param dpName - The name of the data product.
     * @param event - The event triggered by the checkbox.
     */
    const handleItemCheck = (dpName: string, event: React.MouseEvent) => {
        event.stopPropagation();

        let updatedSelectedDs = [...selectedItems];

        if (updatedSelectedDs.includes(dpName)) {
            updatedSelectedDs = updatedSelectedDs.filter((name) => name !== dpName);
        } else {
            updatedSelectedDs.push(dpName);
        }

        // Update isAllSelected based on current page items only
        const currentPageItems = dataSource?.map((item) => item?.id) || [];
        const currentPageSelectedCount = currentPageItems.filter((id) => updatedSelectedDs.includes(id)).length;
        setIsAllSelected(currentPageSelectedCount === currentPageItems.length && currentPageItems.length > 0);

        setSelectedItems(updatedSelectedDs);
    };

    /**
     * Closes the currently open modal and resets related state variables.
     */
    const handleOpenModal = (
        key: string,
        dataProductUrn?: string,
        defaultValues?: BrowseDataProductRelatedEntityType[],
    ) => {
        setModalKey(key);
        if (dataProductUrn) {
            setIndividualDPUrn(dataProductUrn);
        }
        if (defaultValues && defaultValues.length > 0) {
            const defaultvalues: DefaultValueType[] = defaultValues.map((item: BrowseDataProductRelatedEntityType) => {
                return {
                    urn: item.urn,
                    entity: item.entity,
                    ownershipType: item.ownershipType,
                    ownerType: item.ownerType,
                };
            });

            setDefaultValueURNs(defaultvalues);
        }
    };

    /**
     * Deletes selected data products in batch or a single data product if specified.
     *
     * This function performs a batch delete operation for the selected data products.
     * If an individual data product URN is provided, it deletes only that specific data product.
     * Otherwise, it deletes all the data products selected in the `selectedItems` state.
     *
     * The function uses the `batchDeleteDataProductsMutation` GraphQL mutation to perform the delete operation.
     * Upon successful deletion, it displays a success message and refetches the data product listing.
     * If the operation fails, it displays an error message with details.
     *
     * After the operation (success or failure), the modal is closed, and the selection states are reset.
     *
     * @returns {void}
     */
    const batchDelete = () => {
        const urns = individualDPUrn !== null ? [individualDPUrn] : selectedItems;
        batchDeleteDataProductsMutation({
            variables: {
                input: {
                    urns,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({
                        content: t('Successfully Deleted!'),
                        duration: 2,
                    });
                    setTimeout(() => {
                        refecthDataProductListing();
                    }, 500);
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, {
                        content: t('Failed to Update: \n {{message}}', { message: e.message || '' }),
                        duration: 3,
                    }),
                );
            })
            .finally(() => {
                setIsAllSelected(false);
                setSelectedItems([]);
                setIndividualDPUrn(null);
                handleCloseModal();
            });
    };

    /**
     * Maps selected resources to a format suitable for modal operations.
     * @returns An array of resource objects.
     */
    const resourcesMapper = () => {
        return individualDPUrn
            ? [
                  {
                      resourceUrn: individualDPUrn,
                  },
              ]
            : selectedItems.map((item) => ({
                  resourceUrn: item,
              }));
    };

    /**
     * Retrieves the modal for editing tags or terms based on the specified entity type.
     * @param type - The entity type (Tag or GlossaryTerm).
     * @returns The modal component.
     */
    const getEditTagsTermsModal = (type: EntityType) => {
        return (
            <EditTagTermsModal
                type={type}
                visible
                operationType={!defaultValueURNs?.length || !individualDPUrn ? OperationType.ADD : OperationType.EDIT}
                onCloseModal={handleCloseModal}
                refetch={refecthDataProductListing}
                resources={resourcesMapper()}
                defaultValues={defaultValueURNs?.length ? defaultValueURNs : undefined}
            />
        );
    };

    return (
        <>
            {exportEntity && <Notification exportEntity={exportEntity} />}
            {isBulkImportModalVisible && (
                <BulkLoadEntityModal
                    entityType={EntityType.DataProduct}
                    onClose={() => setIsBulkImportModalVisible(false)}
                    expectedHeadersFromApi={
                        expectedXlsxHeadersFromAPI?.getEntityImportValidationMetadata?.validationMetadata.map(
                            (header) => ({
                                ...header,
                                validate: header.validate === null ? undefined : header.validate,
                                sampleRows: header.sampleRows.map((row) => row.map((cell) => cell || '')),
                            }),
                        ) || []
                    }
                />
            )}
            <Card>
                <BrowseDataProductHeader
                    onAddDataProduct={() => {
                        handleOpenModal(ModalKeys.CREATE);
                    }}
                    onImportDataProduct={() => setIsBulkImportModalVisible(true)}
                    onSearch={onSearchItem}
                    onApplyFilter={onApplyFilters}
                    onResetFilter={onResetFilters}
                    data={dataSource}
                    canCreate={canCreate}
                    canBulkImport={canBulkImport}
                />
                <BrowseDataProductTable
                    data={dataSource as DataDataProductTable[]}
                    onModalOpen={handleOpenModal}
                    onSelectAll={handleSelectAll}
                    onSelectItem={handleItemCheck}
                    selectedItems={selectedItems}
                    isAllSelected={isAllSelected}
                    loading={loading}
                    canSelectRows={
                        canDelete || canExport || canUpdateOwners || canUpdateTags || canUpdateTerms || canUpdateDomains
                    }
                    permissions={{
                        canDelete,
                        canUpdateOwners,
                        canUpdateTags,
                        canUpdateTerms,
                        canUpdateDomains,
                    }}
                />
                <Divider className="ma-0" />
                <PaginationContainer>
                    <Pagination
                        total={totalCount}
                        current={page}
                        pageSize={pageSize}
                        onShowSizeChange={(_, size) => {
                            setPageSize(size);
                        }}
                        showSizeChanger
                        pageSizeOptions={BrowseCfg.PAGE_SIZES}
                        showQuickJumper={false}
                        onChange={onChangePage}
                        className="f-pagination-bar"
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                    />
                </PaginationContainer>

                <BulkAction
                    type="defaultType"
                    exportCsv={() => {}}
                    clearSelectedItems={onBulkActionCancel}
                    selectedItems={selectedItems}
                    actionAbleItems={BrowseDataProductBulkActionAbleItems(handleOpenModal, exportXlsx, {
                        canExport,
                        canDelete,
                        canUpdateDomains,
                        canUpdateOwners,
                        canUpdateTags,
                        canUpdateTerms,
                        unauthorisedTooltip: t('UNAUTHORISED_TOOLTIP'),
                    })}
                />
                {modalKey === ModalKeys.UPDATE_OWNERS && (
                    <UpdateOwnersModal
                        onCloseModal={handleCloseModal}
                        refetch={refecthDataProductListing}
                        isVisible
                        resources={resourcesMapper()}
                        defaultValues={defaultValueURNs?.length ? defaultValueURNs : undefined}
                    />
                )}
                {modalKey === ModalKeys.UPDATE_TAGS && getEditTagsTermsModal(EntityType.Tag)}
                {modalKey === ModalKeys.UPDATE_TERMS && getEditTagsTermsModal(EntityType.GlossaryTerm)}
                {modalKey === ModalKeys.UPDATE_DOMAIN && (
                    <SetDomainModal
                        urns={individualDPUrn ? [individualDPUrn] : selectedItems}
                        defaultValue={defaultValueURNs[0]?.entity ? defaultValueURNs[0] : undefined}
                        refetch={refecthDataProductListing}
                        onCloseModal={handleCloseModal}
                    />
                )}
                {modalKey === ModalKeys.DELETE && (
                    <Modal
                        centered
                        closable={false}
                        className="f-dialog"
                        visible={modalKey === ModalKeys.DELETE}
                        footer={
                            <>
                                <Button type="default" onClick={handleCloseModal}>
                                    {t('Cancel')}
                                </Button>
                                <Button disabled={loadingDelete} className="ant-btn-dangerous" onClick={batchDelete}>
                                    {t('Yes, Delete')}
                                </Button>
                            </>
                        }
                    >
                        <h1 className="f-dialog-title">{t('Delete selected data products')}</h1>
                        <p className="f-dialog-text">
                            {t('Are you sure you want to delete the selected data products?')}
                        </p>
                    </Modal>
                )}

                {modalKey === ModalKeys.CREATE && (
                    <CreateDataProductModal
                        onCreateDataProduct={() => {}}
                        refetch={fetchMore}
                        onClose={() => {
                            handleCloseModal();
                        }}
                    />
                )}
            </Card>
        </>
    );
};
